import React from "react";
import { Stack, MultiSelect, Paper, Text, Badge, Group } from "@mantine/core";
import { ServiceAdjustmentForm } from "../../../types/serviceAdjustmentForms";

interface AdjustmentFormsStepProps {
  adjustmentForms: ServiceAdjustmentForm[];
  selectedFormIds: string[];
  setSelectedFormIds: (ids: string[]) => void;
  isMobile: boolean;
}

export default function AdjustmentFormsStep({
  adjustmentForms,
  selectedFormIds,
  setSelectedFormIds,
  isMobile,
}: AdjustmentFormsStepProps) {
  // Filter out forms without IDs and inactive forms unless they're already connected
  const availableForms = adjustmentForms.filter((form) => {
    if (!form.service_adjustment_form_id) return false;
    return (
      form.is_active ||
      selectedFormIds.includes(form.service_adjustment_form_id)
    );
  });

  // Prepare MultiSelect options
  const formOptions = availableForms
    .filter((form) => form.service_adjustment_form_id !== null)
    .map((form) => ({
      value: form.service_adjustment_form_id as string,
      label: form.name,
      disabled:
        !form.is_active &&
        !selectedFormIds.includes(form.service_adjustment_form_id as string),
    }));

  // Get details of selected forms
  const selectedForms = adjustmentForms.filter(
    (form) =>
      form.service_adjustment_form_id &&
      selectedFormIds.includes(form.service_adjustment_form_id),
  );

  return (
    <Stack gap={isMobile ? "sm" : "md"} mt="md">
      <Text size="sm" c="dimmed">
        Connect adjustment forms to modify service pricing and duration based on
        customer selections. These changes will be saved when you click Save.
      </Text>

      <MultiSelect
        label="Connected Forms"
        description="Select forms to connect to this service"
        placeholder={
          adjustmentForms.length === 0
            ? "No adjustment forms available"
            : "Select forms to connect"
        }
        data={formOptions}
        value={selectedFormIds}
        onChange={setSelectedFormIds}
        disabled={adjustmentForms.length === 0}
        searchable
        clearable
        size={isMobile ? "sm" : "md"}
      />

      {selectedForms.length > 0 && (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Connected Forms ({selectedForms.length})
            </Text>
            {selectedForms.map((form) => (
              <Group key={form.service_adjustment_form_id} gap="xs">
                <Badge
                  color={form.is_active ? "teal" : "gray"}
                  variant="light"
                  size="sm"
                >
                  {form.name}
                </Badge>
                <Text size="xs" c="dimmed">
                  {form.questions?.length || 0} question
                  {form.questions?.length !== 1 ? "s" : ""} • Priority:{" "}
                  {form.priority}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}

      {adjustmentForms.length === 0 && (
        <Text size="sm" c="dimmed">
          No adjustment forms have been created yet. Visit the Adjustment Forms
          tab in Settings to create one.
        </Text>
      )}
    </Stack>
  );
}
